import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

interface VectorMatch {
  id: string;
  content: string;
  metadata: Record<string, Json> | null;
  similarity: number;
}

export const generateEmbedding = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        text: z.string().min(1, "text is required"),
        pageContext: z.object({ language: z.string().optional() }).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { text, pageContext } = data;
    const { createClient } = await import("@supabase/supabase-js");
    const { generateDeterministicEmbedding } = await import("./pseudoEmbedding");

    const LOVABLE_API_KEY = process.env["LOVABLE_API_KEY"];
    if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY not configured");

    // Use Lovable AI to generate a pseudo-embedding via structured output.
    // Since the gateway doesn't have a dedicated embedding endpoint,
    // we use the chat model to generate a compact semantic representation.
    const embeddingResponse = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${LOVABLE_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash-lite",
        messages: [
          {
            role: "system",
            content: `You are a semantic analysis engine. Given text, produce a JSON array of exactly 1536 floating point numbers between -1 and 1 that represents the semantic meaning of the input. Focus on: nearshoring, Africa, software development, Egypt, European business, timezone alignment, cost savings, tech talent. Return ONLY the JSON array, nothing else.`,
          },
          { role: "user", content: text.slice(0, 2000) },
        ],
      }),
    });

    let embedding: number[];
    if (!embeddingResponse.ok) {
      const errText = await embeddingResponse.text();
      console.error("Embedding generation failed:", embeddingResponse.status, errText);
      embedding = generateDeterministicEmbedding(text);
    } else {
      const embeddingData = await embeddingResponse.json();
      const embeddingText: string = embeddingData.choices?.[0]?.message?.content || "";
      try {
        const cleaned = embeddingText.replace(/```json\n?|\n?```/g, "").trim();
        embedding = JSON.parse(cleaned);
        if (!Array.isArray(embedding) || embedding.length !== 1536) {
          embedding = generateDeterministicEmbedding(text);
        }
      } catch {
        embedding = generateDeterministicEmbedding(text);
      }
    }

    // Now query the database for similar nodes
    const supabaseUrl = process.env["SUPABASE_URL"] ?? process.env["VITE_SUPABASE_URL"];
    const supabaseServiceKey = process.env["SUPABASE_SERVICE_ROLE_KEY"];
    if (!supabaseUrl || !supabaseServiceKey) {
      throw new Error("Supabase server credentials not configured");
    }
    const supabase = createClient(supabaseUrl, supabaseServiceKey);

    const { data: matches, error } = await supabase.rpc("match_african_nodes", {
      query_embedding: JSON.stringify(embedding),
      match_count: 50,
      match_threshold: 0.05,
    });

    if (error) {
      console.error("Vector search error:", error);
      throw new Error(`Vector search failed: ${error.message}`);
    }

    // Filter by language if pageContext provided
    let filtered = (matches || []) as VectorMatch[];
    if (pageContext?.language) {
      const langFiltered = filtered.filter(
        (m) => !m.metadata?.["language"] || m.metadata["language"] === pageContext.language,
      );
      if (langFiltered.length >= 3) filtered = langFiltered;
    }

    return { matches: filtered.slice(0, 3), embedding };
  });
